<script>
import Layout from '@layouts/main'
import api from '@utils/api'
import Filters from '@views/filters-dialog'
import connectedPlugs from '@src/models/connected-plugs'
import formatDate from '@utils/format-date-with-time'
import TablePaginator from '@components/paginator'
import { mapState } from 'vuex'
import AdjustColumnsDialog from '@components/adjust-columns-dialog'
import { hideValues, updateColumns } from '@utils/helpers/columns'

export default {
  name: 'ConnectedPlugs',
  components: { Layout, Filters, TablePaginator, AdjustColumnsDialog },
  data() {
    return {
      modelName: 'connectedPlugs',
      isLoading: false,
      headers: [
        { text: 'plugGuid', value: 'plugId', sortable: true },
        { text: 'sigfoxId', value: 'sigfoxId', sortable: true, show: true },
        { text: 'sigfoxPac', value: 'sigfoxPac', sortable: true, show: true },
        { text: 'zigbeeId', value: 'zigbeeId', sortable: true, show: true },
        {
          text: 'plug_version',
          value: 'firmwareVersion',
          sortable: true,
          show: true,
        },
        {
          text: 'last_connection',
          value: 'lastConnectionTime',
          sortable: true,
          show: true,
        },
      ],
      rowsPerPage: [10, 25, 50],
      page: 1,
      totalPages: 0,
      sortBy: 'plugId',
      descending: true,
      first: false,
      last: false,
      content: [],
      pagination: {},
      totalElements: 0,
      showFilters: false,
      model: connectedPlugs,
      showDialog: false,
      filters: null,
    }
  },
  computed: {
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending || null,
        ...this.filters,
      }
    },
    goods() {
      return this.$store.getters['data/getDictionary']('goods')
    },
    ...mapState({
      unitsMeasurementValue: state => state.userSettings.userSettings['unitsMeasurement'],
    }),
    unitsMeasurement() {
      return this.$store.getters['userSettings/getUnitsMeasurement']
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: this.modelName,
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.getPaginator()
    this.getFilters()
  },

  methods: {
    fetchData() {
      const id = this.$route.params.id
      this.isLoading = true
      api
        .getSubItems('heartbeat', id, 'plug', this.params)
        .then(response => {
          this.content = response.content
          this.totalPages = response.totalPages
          this.first = response.first
          this.last = response.last
          this.page = response.number + 1
          this.pagination = {
            ...this.pagination,
            page: 1,
            rowsPerPage: response.size,
            sortBy: this.sortBy,
            descending: this.descending,
          }
        })
        .catch(() => {})
        .then(() => {
          this.isLoading = false
        })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    getFilters() {
      const filter = this.$store.getters['filters/getFilters'](this.modelName)
      this.filters = filter ? filter.value : null
      this.showFilters = false
      this.fetchData()
    },
    setFilters() {
      const filter = this.$store.getters['filters/getFilters'](this.modelName)
      this.filters = filter ? filter.value : null
      this.showFilters = false
      this.page = 1
      this.fetchData()
    },
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](this.modelName)
      if (pagination) {
        this.page = pagination.page || 1
        this.sortBy = pagination.sortBy || this.sortBy
        this.descending = pagination.descending || false
        this.pagination.page = 1
        this.pagination.rowsPerPage = pagination.rowsPerPage || 10
      } else {
        this.page = 1
        this.pagination.page = 1
        this.pagination.rowsPerPage = 10
      }
    },
    changePage(value) {
      this.page = value
      this.fetchData()
    },
    changeRowsPerPage(value) {
      if (this.pagination.rowsPerPage === value) return
      this.pagination = {
        ...this.pagination,
        rowsPerPage: value,
      }
      this.page = 1
      this.$store.dispatch('pagination/saveRowsPerPage', value)
      this.fetchData()
    },
    changeSort(header) {
      if (!header.sortable) return
      if (this.sortBy === header.value) {
        this.descending = !this.descending
      } else {
        this.sortBy = header.value
        this.descending = false
      }
      this.fetchData()
    },
    getSortClass(header) {
      return [
        header.sortable ? 'column sortable' : null,
        this.pagination.descending ? 'desc' : 'asc',
        header.value === this.pagination.sortBy ? 'active' : '',
      ]
    },
    dateFormat(date) {
      const formatedDate = formatDate(date)
      return formatedDate === 'Invalid Date' ? '-' : formatedDate
    },
    hideValues(field) {
      return hideValues(field, this.headers)
    },
    updateColumns(columns) {
      updateColumns(columns, this.headers)
    },
    openDialog() {
      this.showDialog = true
    },
  },
}
</script>

<template>
  <Layout>
    <div slot="actionButton">
      <v-btn
        color="info"
        @click="fetchData"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn
        :color="filters ? `success` : `info`"
        @click="showFilters = true"
      >
        <v-icon>filter_list</v-icon>
      </v-btn>
    </div>
    <v-layout justify-end>
      <v-btn
        icon
        flat
        @click="openDialog()">
        <v-icon>view_column</v-icon>
      </v-btn>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="content"
      :pagination="pagination"
      :total-items="totalElements"
      :loading="isLoading"
      :class="$style.sdTable"
      hide-actions
    >
      <template
        slot="headers"
        slot-scope="props">
        <tr>
          <th
            v-for="header in headers"
            :key="header.text"
            :class="getSortClass(header)"
            :style="[header.show === false ? {'display': 'none'} : {'text-align': 'left'}]"
            @click="changeSort(header)"
          >
            <span> {{ $t(`label__${header.text}`) }} </span>
            <v-icon
              v-if="header.sortable"
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td>{{ props.item.plugId }}</td>
          <td v-if="!hideValues('sigfoxId')">{{ props.item.sigfoxId }}</td>
          <td v-if="!hideValues('sigfoxPac')">{{ props.item.sigfoxPac }}</td>
          <td v-if="!hideValues('zigbeeId')">{{ props.item.zigbeeId }}</td>
          <td v-if="!hideValues('firmwareVersion')">{{ props.item.firmwareVersion }}</td>
          <td v-if="!hideValues('lastConnectionTime')">{{ dateFormat(props.item.lastConnectionTime) }}</td>
        </tr>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)" />
    <adjust-columns-dialog
      v-model="showDialog"
      :headers="headers.filter(h => h.show !== undefined)"
      :model-name="modelName"
      @columns-updated="updateColumns"
    />
    <filters
      :visible="showFilters"
      :model="model"
      :model-name="modelName"
      @close-modal="showFilters = false"
      @filters-confirmed="setFilters()"
    />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
